import {
  appendVisibilitySettingsV3,
  ChipRendererV3Component,
  ColumnDefinitionV3,
  ColumnRendererComponentV3,
  ReadOnlyChipTypeV3,
  SelectFilterV3Component,
} from '@gea/digital-ui-lib';
import { MachineNameColumnRendererComponent } from '../machine-name-column-renderer/machine-name-column-renderer.component';
import { CommonAlarmRendererComponent } from '../common-alarm-renderer/common-alarm-renderer.component';
import { TranslateService } from '@ngx-translate/core';
import { AssetProduct } from '@generated-apis/index';

export type Machine = Partial<AssetProduct>;
export type Organisations = Set<string>;
export const DASHBOARD_TABLE_ID = 'SFT-MachineDashboardTable';

export type Status =
  | 'Standstill'
  | 'Startup'
  | 'Slowing down'
  | 'Standby'
  | 'Closing Program'
  | 'Partial Ejection'
  | 'Total Ejection'
  | 'Overflow'
  | 'Production'
  | 'CIP';

export const possibleStatuses: Status[] = [
  'Standstill',
  'Startup',
  'Slowing down',
  'Standby',
  'Closing Program',
  'Partial Ejection',
  'Total Ejection',
  'Overflow',
  'Production',
  'CIP',
];

export type Severity = ReadOnlyChipTypeV3;

export function statusToSeverity(status: Status | null): Severity {
  switch (status) {
    case 'Standstill':
      return 'default';
    case 'CIP':
      return 'information';
    case 'Standby':
      return 'progress';
    case 'Startup':
    case 'Slowing down':
    case 'Closing Program':
    case 'Partial Ejection':
    case 'Total Ejection':
    case 'Overflow':
    case 'Production':
      return 'success';
    default:
      return 'default';
  }
}

export type StatusMapping = {
  severity?: Severity;
  text?: string;
};

export const StatusConfig: Record<Status, StatusMapping | {}> = possibleStatuses.reduce<Record<string, StatusMapping>>(
  (acc, status) => {
    acc[status] = {
      severity: statusToSeverity(status),
      text: status,
    };
    return acc;
  },
  {} as Record<string, StatusMapping>
);

// in case the backend does not return any status, handle null
StatusConfig['null' as Status] = {};

export type StatusOption = {
  name: string;
  value: string;
  selected: boolean;
};

export const StatusFilterOptions: StatusOption[] = possibleStatuses.map((status) => {
  return {
    name: status,
    value: status,
    selected: false,
  };
});

export const columnDefinitions = (organisations: Organisations, translate: TranslateService): ColumnDefinitionV3[] =>
  appendVisibilitySettingsV3([
    {
      displayName: translate.instant('MACHINE-LIST.TABLE-COLUMN-HEADER.NAME'),
      key: 'displayName',
      width: 450,
      renderer: {
        component: MachineNameColumnRendererComponent as ColumnRendererComponentV3<unknown>,
      },
    },
    {
      displayName: translate.instant('MACHINE-LIST.TABLE-COLUMN-HEADER.NICKNAME'),
      key: 'machineNickname',
      width: 300,
    },
    {
      displayName: translate.instant('MACHINE-LIST.TABLE-COLUMN-HEADER.STATUS'),
      key: 'status',
      width: 300,
      renderer: {
        component: ChipRendererV3Component as ColumnRendererComponentV3<unknown>,
        config: {
          mapping: StatusConfig,
        },
      },
      filter: {
        component: SelectFilterV3Component,
        config: {
          options: StatusFilterOptions,
        },
      },
    },
    {
      displayName: translate.instant('MACHINE-LIST.TABLE-COLUMN-HEADER.ORGANIZATION'),
      key: 'organization',
      width: 300,
      filter: {
        component: SelectFilterV3Component,
        config: {
          options: Array.from(organisations).map((organization) => ({
            name: organization,
            value: organization,
            selected: false,
          })),
        },
      },
    },
    {
      displayName: translate.instant('MACHINE-LIST.TABLE-COLUMN-HEADER.COMMON-ALARM'),
      key: 'commonAlarm',
      width: 300,
      resizeable: false,
      renderer: {
        component: CommonAlarmRendererComponent as ColumnRendererComponentV3<unknown>,
      },
      filter: {
        component: SelectFilterV3Component,
        config: {
          options: [
            { name: 'Is empty (or 0)', value: 'false', selected: false },
            { name: 'Is not empty (or 0)', value: 'true', selected: false },
          ],
        },
      },
    },
  ]);
